import React from 'react';

import { getProjectBrief, getProjectBriefVariables } from '__generated__/getProjectBrief';
import { ProjectBriefFields } from '__generated__/ProjectBriefFields';
import { AuthConsumer } from 'projects/components/AuthContext';
import FilterDescription from 'projects/components/FilterDescription';
import Brief from 'projects/routes/Brief';
import BriefQuery from 'projects/routes/BriefQuery';
import InviteDesignersPage from 'projects/routes/InviteDesignersPage';
import { Industries } from 'projects/util/industries';
import { createStepState, StepConfiguration } from 'projects/util/step';
import { ApolloConsumer } from 'react-apollo';
import { RouteComponentProps, withRouter } from 'react-router';
import { Route, Switch } from 'react-router-dom';
import AboutYouPage from './AboutYouPage';
import DetailsPage from './DetailsPage';
import queries from './ProjectBrief.graphql';
import SummaryPage from './SummaryPage';
import TimeAndBudgetPage from './TimeAndBudgetPage';

type Props = RouteComponentProps<{
    briefId: string;
    step: string;
}>;

const steps: StepConfiguration[] = [
    {
        step: 1,
        title: __('Tell us about your project'),
    },
    { step: 2, title: __('Your time & budget') },
    { step: 3, title: __('About you') },
    { step: 4, title: __('Review your brief') },
    {
        step: 5,
        title: __('Invite designers to quote'),
        requiresAuth: true,
        isDesignerStep: true,
        nextActionLabel: __('Confirm designers'),
    },
];

export const ProjectBrief: React.FunctionComponent<Props> = props => (
    <BriefQuery<getProjectBrief, getProjectBriefVariables>
        query={queries.getProjectBrief}
        variables={{ id: props.match.params.briefId }}
        fieldsTypename="ProjectBriefFields"
    >
        {(data, refetch) => {
            const step = createStepState({
                steps,
                brief: data.brief,
                currentStep: parseInt(props.match.params.step, 10),
                designerSources: data.brief.designersWithSource.map(d => d.source),
            });

            const brief = {
                ...data.brief,
                fields: data.brief.fields as ProjectBriefFields,
            };

            const industries = new Industries(data.industries);

            return (
                <ApolloConsumer>
                    {client => (
                        <AuthConsumer>
                            {({ login }) => (
                                <Brief
                                    refetch={refetch}
                                    schema="project"
                                    brief={brief}
                                    viewer={data.viewer}
                                    login={login}
                                    step={step}
                                    client={client}
                                >
                                    {form => (
                                        <Switch>
                                            <Route
                                                path={`/briefs/projects/:briefId/1`}
                                                render={() => (
                                                    <DetailsPage
                                                        form={form}
                                                        brief={brief}
                                                        categories={data.categories}
                                                    />
                                                )}
                                            />
                                            <Route
                                                path={`/briefs/projects/:briefId/2`}
                                                render={() => (
                                                    <TimeAndBudgetPage
                                                        form={form}
                                                        brief={brief}
                                                        site={data.site}
                                                    />
                                                )}
                                            />
                                            <Route
                                                path={`/briefs/projects/:briefId/3`}
                                                render={() => (
                                                    <AboutYouPage
                                                        form={form}
                                                        brief={brief}
                                                        viewer={data.viewer}
                                                        industries={data.industries}
                                                    />
                                                )}
                                            />
                                            <Route
                                                path={`/briefs/projects/:briefId/4`}
                                                render={() => (
                                                    <SummaryPage
                                                        brief={brief}
                                                        industries={industries}
                                                    />
                                                )}
                                            />
                                            <Route
                                                path={`/briefs/projects/:briefId/5`}
                                                render={() => (
                                                    <InviteDesignersPage
                                                        brief={brief}
                                                        viewer={data.viewer}
                                                    >
                                                        <FilterDescription
                                                            industries={data.industries}
                                                            brief={brief}
                                                            category={
                                                                brief.category! &&
                                                                brief.category!.title
                                                            }
                                                            budget={brief.fields.budget}
                                                        />
                                                    </InviteDesignersPage>
                                                )}
                                            />
                                        </Switch>
                                    )}
                                </Brief>
                            )}
                        </AuthConsumer>
                    )}
                </ApolloConsumer>
            );
        }}
    </BriefQuery>
);

export default withRouter(ProjectBrief);
